import { useState, type FormEvent } from "react";
import { postJson } from "../lib/api";
import { TilePicker, TileMultiPicker, type TileOption } from "./FormTiles";

type Mode = "in_person" | "remote" | "both";
type Availability = "weekdays" | "weekends" | "evenings" | "flexible";

const MODE: TileOption<Mode>[] = [
  {
    value: "in_person",
    label: "In person",
    hint: "Visit the home in Tirunelveli",
  },
  {
    value: "remote",
    label: "Remotely",
    hint: "From anywhere — online / on call",
  },
  {
    value: "both",
    label: "Both",
    hint: "Happy to mix the two",
  },
];

const AVAILABILITY: TileOption<Availability>[] = [
  { value: "weekdays", label: "Weekdays" },
  { value: "weekends", label: "Weekends" },
  { value: "evenings", label: "Evenings" },
  { value: "flexible", label: "Flexible" },
];

const INTERESTS: TileOption[] = [
  { value: "elder_care", label: "Elder companionship" },
  { value: "child_mentoring", label: "Tutoring / mentoring" },
  { value: "medical", label: "Medical / healthcare" },
  { value: "legal", label: "Legal / paperwork" },
  { value: "meals", label: "Meal preparation" },
  { value: "admin", label: "Admin & operations" },
  { value: "events", label: "Events & outreach" },
  { value: "translation", label: "Translation" },
  { value: "tech", label: "Tech / website help" },
];

interface FormState {
  fullName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  mode: Mode | "";
  availability: Availability | "";
  hoursPerWeek: string;
  interests: string[];
  skills: string;
  consent: boolean;
}

const INITIAL: FormState = {
  fullName: "",
  email: "",
  phone: "",
  city: "",
  state: "Tamil Nadu",
  mode: "",
  availability: "",
  hoursPerWeek: "",
  interests: [],
  skills: "",
  consent: false,
};

export default function VolunteerForm() {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<
    { kind: "idle" } | { kind: "success" } | { kind: "error"; msg: string }
  >({ kind: "idle" });

  const upd = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const toggle = (v: string) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(v)
        ? f.interests.filter((x) => x !== v)
        : [...f.interests, v],
    }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ kind: "error", msg: "Please consent to being contacted." });
      return;
    }
    if (!form.mode) {
      setStatus({
        kind: "error",
        msg: "Tell us how you'd like to volunteer.",
      });
      return;
    }
    if (form.interests.length === 0) {
      setStatus({
        kind: "error",
        msg: "Please select at least one area of interest.",
      });
      return;
    }
    setSubmitting(true);
    setStatus({ kind: "idle" });

    const res = await postJson("/api/volunteer", { ...form });

    setSubmitting(false);
    if (res.ok) {
      setStatus({ kind: "success" });
      setForm(INITIAL);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setStatus({ kind: "error", msg: res.error });
    }
  };

  if (status.kind === "success") {
    return (
      <div className="surface relative overflow-hidden animate-fadeUp">
        <div
          aria-hidden
          className="absolute -top-20 -right-20 h-60 w-60 rounded-full blur-3xl opacity-60"
          style={{ background: "radial-gradient(closest-side, #aee8c7, transparent)" }}
        />
        <span className="pill pill-mint">Welcome — thank you</span>
        <h3 className="text-3xl font-display mt-4 text-ink-900">
          Your registration is with our team.
        </h3>
        <p className="text-ink-500 mt-3 leading-relaxed">
          We'll reach out with next steps — orientation, scheduling, and how
          your interests fit current needs.
        </p>
        <button
          type="button"
          onClick={() => setStatus({ kind: "idle" })}
          className="btn-ghost mt-6"
        >
          Submit another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="surface space-y-7" noValidate>
      <section>
        <div className="field-label">How would you like to volunteer? *</div>
        <TilePicker
          accent="mint"
          value={form.mode}
          onChange={(v) => upd("mode", v)}
          options={MODE}
          columns={3}
        />
      </section>

      <section className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="v-fullName">Full name *</label>
          <input
            id="v-fullName"
            className="field-input"
            required
            value={form.fullName}
            onChange={(e) => upd("fullName", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="v-email">Email *</label>
          <input
            id="v-email"
            type="email"
            className="field-input"
            required
            value={form.email}
            onChange={(e) => upd("email", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="v-phone">Phone *</label>
          <input
            id="v-phone"
            type="tel"
            className="field-input"
            required
            value={form.phone}
            onChange={(e) => upd("phone", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="v-city">City *</label>
          <input
            id="v-city"
            className="field-input"
            required
            value={form.city}
            onChange={(e) => upd("city", e.target.value)}
          />
        </div>
      </section>

      <section>
        <div className="field-label">Availability</div>
        <TilePicker
          accent="mint"
          value={form.availability}
          onChange={(v) => upd("availability", v)}
          options={AVAILABILITY}
          columns={4}
        />
      </section>

      <section>
        <label className="field-label" htmlFor="v-hours">
          Hours per week you can give
        </label>
        <input
          id="v-hours"
          className="field-input"
          placeholder="e.g. 4"
          inputMode="numeric"
          value={form.hoursPerWeek}
          onChange={(e) => upd("hoursPerWeek", e.target.value)}
        />
      </section>

      <section>
        <div className="field-label">Areas of interest *</div>
        <TileMultiPicker
          accent="mint"
          values={form.interests}
          onToggle={toggle}
          options={INTERESTS}
          columns={3}
        />
      </section>

      <section>
        <label className="field-label" htmlFor="v-skills">
          Skills or background we should know about
        </label>
        <textarea
          id="v-skills"
          className="field-textarea"
          placeholder="Languages, professional training, prior volunteer experience, etc."
          value={form.skills}
          onChange={(e) => upd("skills", e.target.value)}
        />
      </section>

      <label className="flex items-start gap-3 text-sm text-ink-700 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-ink-300 text-royal focus:ring-royal"
          checked={form.consent}
          onChange={(e) => upd("consent", e.target.checked)}
        />
        <span>
          I consent to Sanctology Welfare Foundation contacting me about
          volunteer opportunities. *
        </span>
      </label>

      {status.kind === "error" && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-glow animate-fadeUp">
          {status.msg}
        </div>
      )}

      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "Submitting…" : "Register to volunteer →"}
      </button>
    </form>
  );
}
